import type { Language } from "@/lib/i18n/types";
import type { BriefTranslations } from "@/lib/i18n/types/brief";
import type {
  CheckTriggerKind,
  DocumentCitation,
  FindingCitation,
  FindingSeverity,
  FindingStatus,
} from "./types";

/** Whether a citation points into a document (the brief's or a checked file). */
export function isDocumentCitation(c: FindingCitation): c is DocumentCitation {
  return c.role === "brief" || c.role === "document";
}

/**
 * Pure presentation helpers for Franklin's suggestions, appendices and check
 * runs, kept out of the components so they can be unit-tested and so the
 * list and the digest cannot disagree.
 */

/** The severity dot's colour — shared by the Brief page's cards and the dashboard card. */
export const SEVERITY_DOT: Record<FindingSeverity, string> = {
  high: "bg-primary",
  medium: "bg-amber-500",
  low: "bg-slate-400",
  info: "bg-slate-300",
};

const SEVERITY_ORDER: Record<FindingSeverity, number> = { high: 0, medium: 1, low: 2, info: 3 };

/** Open findings: worst first, then newest. */
export function bySeverityThenNewest(
  a: { severity: FindingSeverity; created_at: string },
  b: { severity: FindingSeverity; created_at: string }
): number {
  const s = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
  if (s !== 0) return s;
  return b.created_at.localeCompare(a.created_at);
}

/** The label for a settled suggestion; null while it is open. */
export function statusLabel(
  t: BriefTranslations,
  finding: { status: FindingStatus }
): string | null {
  switch (finding.status) {
    case "accepted":
      return t.findings.status.accepted;
    case "dismissed":
      return t.findings.status.dismissed;
    case "resolved":
      // Only a check run resolves a suggestion.
      return t.findings.status.resolved;
    default:
      return null;
  }
}

export function triggerLabel(t: BriefTranslations, kind: CheckTriggerKind): string {
  switch (kind) {
    case "document_processed":
      return t.run.trigger.documentProcessed;
    case "brief_changed":
      return t.run.trigger.briefChanged;
    case "issue_activity":
      return t.run.trigger.issueActivity;
    case "building_data":
      return t.run.trigger.buildingData;
    default:
      return t.run.trigger.manual;
  }
}

const LOCALE: Record<Language, string> = { en: "en-GB", et: "et-EE" };

export function formatDateTime(iso: string | null | undefined, language: Language): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(LOCALE[language] ?? "en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function formatDate(iso: string | null | undefined, language: Language): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(LOCALE[language] ?? "en-GB", { dateStyle: "medium" }).format(date);
}
